import React from "react";
import { RESUME_DATA } from "@/constants/resumeData";

const ResumePage: React.FC = () => {
  const {
    personalInfo,
    summary,
    workExperience,
    technicalSkills,
    projects,
    education,
    languages,
    achievements,
  } = RESUME_DATA;

  const skillGroups = [
    ["Frontend", technicalSkills.frontend],
    ["Backend", technicalSkills.backend],
    ["Databases", technicalSkills.databases],
    ["Tools", technicalSkills.tools],
    ["AI & Automation", technicalSkills.ai],
  ];

  return (
    <main className="min-h-screen bg-[#ece8df] px-5 py-8 text-[#181818] print:bg-white print:px-0 print:py-0">
      <article className="mx-auto max-w-5xl bg-[#fffdf8] p-8 shadow-sm print:max-w-none print:shadow-none md:p-12">
        <header className="flex flex-wrap items-start justify-between gap-6 border-b border-black/15 pb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-black/45">
              Resume
            </p>
            <h1
              className="mt-3 uppercase leading-none"
              style={{
                fontFamily: "'League Gothic', sans-serif",
                fontSize: "clamp(4rem, 10vw, 8rem)",
              }}
            >
              {personalInfo.name}
            </h1>
            <p className="mt-4 max-w-2xl text-sm uppercase tracking-[0.12em] text-black/60">
              {personalInfo.title}
            </p>
          </div>

          <button
            onClick={() => window.print()}
            className="rounded-full bg-black px-5 py-3 text-xs uppercase tracking-[0.2em] text-white print:hidden"
          >
            Save PDF
          </button>
        </header>

        <section className="grid gap-8 border-b border-black/15 py-8 md:grid-cols-[0.8fr_1.2fr]">
          <div className="space-y-2 text-sm text-black/65">
            <p>{personalInfo.email}</p>
            <p>{personalInfo.phone}</p>
            <p>{personalInfo.location}</p>
            <p>{personalInfo.linkedin}</p>
            <p>{personalInfo.github}</p>
            <p>{personalInfo.portfolio}</p>
          </div>
          <p className="text-sm leading-7 text-black/70">{summary}</p>
        </section>

        <section className="grid gap-8 border-b border-black/15 py-8 md:grid-cols-[0.8fr_1.2fr]">
          <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-black/45">
            Experience
          </h2>
          <div className="space-y-8">
            {workExperience.map((item) => (
              <div key={item.company}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{item.position}</h3>
                    <p className="text-sm text-black/55">{item.company}</p>
                  </div>
                  <p className="text-sm text-black/55">{item.duration}</p>
                </div>
                <ul className="mt-4 grid gap-2 text-sm leading-6 text-black/68">
                  {item.responsibilities.map((responsibility) => (
                    <li key={responsibility}>{responsibility}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-8 border-b border-black/15 py-8 md:grid-cols-[0.8fr_1.2fr]">
          <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-black/45">
            Projects
          </h2>
          <div className="space-y-7">
            {projects.map((project) => (
              <div key={project.name}>
                <h3 className="font-semibold">{project.name}</h3>
                <p className="mt-2 text-sm leading-6 text-black/68">
                  {project.description}
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.15em] text-black/45">
                  {project.technologies.join(" / ")}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-8 border-b border-black/15 py-8 md:grid-cols-[0.8fr_1.2fr]">
          <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-black/45">
            Skills
          </h2>
          <div className="grid gap-4 text-sm text-black/68">
            {skillGroups.map(([label, skills]) => (
              <p key={label as string}>
                <span className="font-semibold text-black">{label}: </span>
                {(skills as string[]).join(", ")}
              </p>
            ))}
          </div>
        </section>

        <section className="grid gap-8 py-8 md:grid-cols-[0.8fr_1.2fr]">
          <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-black/45">
            Education
          </h2>
          <div className="grid gap-5 text-sm text-black/68">
            {education.map((item) => (
              <div key={item.degree}>
                <h3 className="font-semibold text-black">{item.degree}</h3>
                <p>{item.institution}</p>
                <p>{item.duration}</p>
              </div>
            ))}
            <p>
              <span className="font-semibold text-black">Languages: </span>
              {languages
                .map((language) => `${language.name} (${language.proficiency})`)
                .join(", ")}
            </p>
            <ul className="grid gap-2">
              {achievements.map((achievement) => (
                <li key={achievement}>{achievement}</li>
              ))}
            </ul>
          </div>
        </section>
      </article>
    </main>
  );
};

export default ResumePage;
